import { useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { BookOpen, Settings, ShoppingBag, Sunrise, UtensilsCrossed } from 'lucide-react'
import { Administracion } from './screens/Administracion'
import { Ajustes } from './screens/Ajustes'
import { Compra } from './screens/Compra'
import { Hoy } from './screens/Hoy'
import { Login } from './screens/Login'
import { Platos } from './screens/Platos'
import { Recetas } from './screens/Recetas'
import { AvisoProvider } from './components/Aviso'
import { NavegacionContext, type PestanaId } from './lib/navegacion'
import { useSession } from './lib/session'
import { useTema } from './lib/tema'

const PESTANAS = [
  { id: 'hoy', label: 'Hoy', Icono: Sunrise, Pantalla: Hoy },
  { id: 'platos', label: 'Planificación', Icono: UtensilsCrossed, Pantalla: Platos },
  { id: 'compra', label: 'Compra', Icono: ShoppingBag, Pantalla: Compra },
  { id: 'recetas', label: 'Recetas', Icono: BookOpen, Pantalla: Recetas },
] as const

const CONTENEDOR = 'mx-auto w-full max-w-3xl px-4 pt-[calc(1.5rem+env(safe-area-inset-top))]'

export function App() {
  const { user, loading } = useSession()
  // Montado siempre, también en el login, para seguir al teléfono si cambia de tema.
  useTema()
  const [pestana, setPestana] = useState<PestanaId>('hoy')

  if (loading) return <div className="min-h-dvh" />
  if (!user) {
    return (
      <div className={`${CONTENEDOR} pb-12`}>
        <Login />
      </div>
    )
  }

  const Pantalla =
    PESTANAS.find((tab) => tab.id === pestana)?.Pantalla ??
    (pestana === 'admin' && user.isAdmin ? Administracion : Ajustes)

  function irA(destino: PestanaId): void {
    setPestana(destino)
    window.scrollTo({ top: 0 })
  }

  return (
    <NavegacionContext.Provider value={irA}>
      <AvisoProvider>
        <div className={`${CONTENEDOR} relative pb-[calc(6.5rem+env(safe-area-inset-bottom))]`}>
          <button
            type="button"
            aria-label="Ajustes"
            aria-current={pestana === 'ajustes' || pestana === 'admin' ? 'page' : undefined}
            onClick={() => irA('ajustes')}
            className={`absolute right-3 top-[calc(0.75rem+env(safe-area-inset-top))] z-10 grid h-11 w-11 place-items-center rounded-full outline-none transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring ${
              pestana === 'ajustes' || pestana === 'admin' ? 'text-primary' : 'text-muted-foreground'
            }`}
          >
            <Settings className="h-5 w-5" strokeWidth={1.8} />
          </button>

          <AnimatePresence mode="wait" initial={false}>
            <motion.main
              key={pestana}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
            >
              <Pantalla />
            </motion.main>
          </AnimatePresence>
        </div>

        <nav
          aria-label="Secciones"
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border/70 bg-card/90 backdrop-blur-md"
        >
          {/* Planificación es la etiqueta larga: su columna es más ancha para que no pise a sus vecinas en un móvil estrecho. */}
          <div className="mx-auto grid max-w-3xl grid-cols-[1fr_1.4fr_1fr_1fr] pb-[env(safe-area-inset-bottom)]">
            {PESTANAS.map(({ id, label, Icono }) => {
              const activa = pestana === id
              return (
                <button
                  key={id}
                  type="button"
                  aria-current={activa ? 'page' : undefined}
                  onClick={() => irA(id)}
                  className="relative isolate flex min-h-16 flex-col items-center justify-center gap-1 px-1 outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  {activa && (
                    <motion.span
                      layoutId="pestana-activa"
                      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                      className="absolute inset-x-1 inset-y-2 -z-10 rounded-full bg-accent"
                    />
                  )}
                  <Icono
                    aria-hidden="true"
                    className={`h-5 w-5 ${activa ? 'text-primary' : 'text-muted-foreground'}`}
                    strokeWidth={activa ? 2.2 : 1.8}
                  />
                  <span className={`label-caps tracking-[0.06em] whitespace-nowrap ${activa ? 'text-primary' : 'text-muted-foreground'}`}>
                    {label}
                  </span>
                </button>
              )
            })}
          </div>
        </nav>
      </AvisoProvider>
    </NavegacionContext.Provider>
  )
}
